import { ProjectColoredLink } from "@selector/shared/components";
import { type FC, type JSX, useCallback, useMemo } from "react";
import { titleCase } from "title-case";
import { Card, CardRowSimple, type GridCard, HStack, LoadingSpinner, Tag, type TagIntent, TimeAgo } from "widgets";

import type { BlastJob } from "@/graphql.ts";
import { JobStatusEnum } from "@/graphql.ts";
import { matchStatusToTagIntent } from "@/modules/jobs/utils/statusToTag.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { EditPlaybookEventButton } from "@/ui-modules/core/EditPlaybookEventButton.tsx";

import style from "../../core/cards/styles/Card.module.scss";

export interface IJobStatusCardProps extends GridCard {
  job: BlastJob;
}

const JobStatusCard: FC<IJobStatusCardProps> = (props) => {
  const job: BlastJob = props.job;

  const project = job.event?.project ? (
    <ProjectColoredLink project={job.event?.project} />
  ) : (
    <>You do not have access to this {getProjectLabel(false, false)}.</>
  );

  const renderStatusText = useCallback(
    (myJob: BlastJob): JSX.Element | null => {
      let textParagraph: JSX.Element | null = null;

      if (myJob.status === JobStatusEnum.Running) {
        textParagraph = (
          <>
            Your job is being processed.
            <span className={style.description}>
              You may navigate away from this page. You will receive a notification when this job has finished.
            </span>
          </>
        );
      } else if (myJob.status === JobStatusEnum.Queued || myJob.status === JobStatusEnum.Initializing) {
        textParagraph = (
          <>
            Your job has been queued and will be processed shortly.
            <span className={style.description}>
              You may navigate away from this page. You will receive a notification when this job has finished.
            </span>
          </>
        );
      } else if (myJob.status === JobStatusEnum.Error) {
        textParagraph = (
          <>
            Your job could not be completed successfully.
            <span className={style.description}>{job.errorDescription}</span>
          </>
        );
      }

      return textParagraph;
    },
    [job.errorDescription],
  );

  const statusIntent = useMemo<TagIntent>((): TagIntent => {
    return matchStatusToTagIntent(job.status).intentColor as TagIntent;
  }, [job.status]);

  return (
    <Card
      title={"Status"}
      actions={[
        job.event && (
          <EditPlaybookEventButton
            eventId={job.event.id}
            key={`edit-playbook-button-on-card`}
            eventOwner={job.event.owner}
          />
        ),
      ]}
    >
      <CardRowSimple label={getProjectLabel(true, false)} value={project} />
      <CardRowSimple label={"Event name"}>{job.eventName}</CardRowSimple>
      <CardRowSimple label={"Event description"}>{job.eventDescription}</CardRowSimple>
      {job.created && <CardRowSimple label={"Submitted"} value={<TimeAgo dateTimeISO={job.created} />} />}
      <CardRowSimple label={"Owner"} value={job.owner.display} />
      <CardRowSimple label={"Status"}>
        <div style={{ display: "inline-flex" }}>
          <HStack spacing={10}>
            {job.status !== JobStatusEnum.Error && job.status !== JobStatusEnum.Completed && (
              <LoadingSpinner size={20} />
            )}
            <Tag intentColor={statusIntent}>{titleCase(job.status.toLowerCase())}</Tag>
          </HStack>
        </div>
        {renderStatusText(job)}
      </CardRowSimple>
    </Card>
  );
};

export { JobStatusCard };
